import { Box, VBox } from "../components-v2/Box";
import { FC, forwardRef, useEffect, useRef, useState } from "react";
import { TMessage } from "../i18n/tt";
import { observer } from "mobx-react";
import { TextArea } from "../components-v2/TextArea";
import { Dialog } from "../components-v2/Dialog";
import { AIModel } from "./AIAssistant";
import { isFailure, Result } from "../common/common";
import { Alert } from "../components-v2/Alert";
import { LoadingIndicator } from "../components-v2/LoadingIndicator";
import { Button } from "../components-v2/Button";
import { isMobileView } from "../utils/screen";

import { useTranslation } from "../stores/RootStoreContext";
import { BoxProps } from "../components-v2/boxHelpers";
import { Language } from "../i18n/languages";

type AIProgressBarProps = BoxProps & {
  message: string;
  finished: boolean;
  onCancel: () => void;
  onApply: () => void;
};

export const AIProgressBar = observer(
  forwardRef<HTMLDivElement, AIProgressBarProps>(function AIProgressBar(
    { message, finished, onCancel, onApply },
    ref,
  ) {
    const [progress, setProgress] = useState("");
    const index = useRef(0);
    const { t } = useTranslation();
    useEffect(() => {
      function tick() {
        setProgress((current) => current + message[index.current]);
        index.current++;
      }

      if (index.current < message.length) {
        const addChar = setInterval(tick, 50);
        return () => clearInterval(addChar);
      }
    }, [message, progress]);

    return (
      <VBox border rounded bg ref={ref}>
        <Box
          padding={"sm"}
          borderBottom
          justifyContent={"space-between"}
          vertical={isMobileView()}
          wrap={!isMobileView()}
        >
          <Box>
            d’Bot {!finished && <LoadingIndicator size="sm" label={t("ai.improve.text.loading")} />}
          </Box>
          <Box vertical={isMobileView()} wrap={!isMobileView()}>
            <Button
              variant={"primary"}
              label={t("ai.button.keep")}
              icon={"check"}
              onClick={onApply}
              size={"sm"}
              disabled={!finished}
            />
            <Button
              label={t("ai.button.cancel")}
              icon={"arrow-counter-clockwise"}
              onClick={onCancel}
              size={"sm"}
              variant={"secondary"}
            />
          </Box>
        </Box>
        <Box padding fg={!finished && "subtle"}>
          {finished ? (
            <div>
              {message.split("\n").map((i, key) => (
                <div key={key}>
                  {i}
                  <br />
                </div>
              ))}
            </div>
          ) : (
            <code>{progress}</code>
          )}
        </Box>
      </VBox>
    );
  }),
);

export const AIAssistantError: FC<{ result?: Result<string, TMessage> }> = observer(
  ({ result }) => {
    const { t } = useTranslation();
    return isFailure(result) ? <Alert type={"error"} message={<>{t(result.error)}</>} /> : null;
  },
);

export const AICustomQueryDialog: FC<{ aiAssistant: AIModel; language: Language }> = observer(
  ({ aiAssistant, language }) => {
    const { t } = useTranslation();
    return aiAssistant.showCustomDialog ? (
      <Dialog
        title={t("ai.customQuery")}
        onSubmit={() => aiAssistant.submitCustomQuery(language)}
        submitLabel={t("send")}
        onCancel={() => aiAssistant.hideCustomDialog()}
      >
        <span>
          <i>
            {t("ai.note")}{" "}
            <a href="https://dcompany.no/feature/dokumentrobot/">{t("ai.note.documentation")}</a>.
          </i>
        </span>
        <Box dropShadow paddingTop>
          <TextArea
            value={aiAssistant.customAction.inputValue}
            onChange={(e) => aiAssistant.customAction.setInputValue(e.target.value)}
            onBlur={() => aiAssistant.customAction.commit()}
          />
        </Box>
      </Dialog>
    ) : null;
  },
);
